<template>
  <div class="operation-log mx-auto">
    <div class="log-header d-flex align-center px-4 py-2">
      <v-icon icon="mdi-history" class="mr-2" color="primary" />
      <span class="text-subtitle-1 font-weight-medium text-primary">操作日志</span>
    </div>

    <v-divider />

    <div class="log-content pa-4">
      <v-list v-if="logs.length > 0" class="log-list">
        <v-list-item
          v-for="(log, index) in logs"
          :key="index"
          class="log-item"
          :class="{ 'grey lighten-5': index % 2 === 0 }"
        >
          <div class="d-flex align-center log-row">
            <!-- 时间 -->
            <div class="log-time text-grey">{{ formatTime(log.createTime) }}</div>
            
            <!-- 分隔符 -->
            <v-divider vertical class="mx-2" />
            
            <!-- 操作人 -->
            <div class="log-operator">{{ log.nickname || '未知用户' }}</div>
            
            <!-- 分隔符 -->
            <v-divider vertical class="mx-2" />
            
            <!-- 操作类型 -->
            <v-chip
              :color="getOperationColor(log.operate)"
              size="small"
              class="mr-2"
              label
            >
              {{ getOperationText(log.operate) }}
            </v-chip>
            
            <!-- 图片链接 -->
            <div class="log-image text-truncate">
              <template v-if="log.operate === 1">
                <a v-if="log.target.toLowerCase().endsWith('.dwg')" href="#" class="image-link" @click.prevent="viewDwg(log.target)">查看原文件</a>
                <a v-else href="#" class="image-link" @click.prevent="previewImage(log.target)">查看原图</a>
                <span class="mx-1">→</span>
                <a href="#" v-if="log.img.endsWith('dwg')" class="image-link" @click.prevent="viewDwg(log.img)">查看新文件</a>
                <a href="#" v-else class="image-link" @click.prevent="previewImage(log.img)">查看新图</a>
              </template>
              <template v-else-if="log.operate === 0 || log.operate === 2">
                <a href="#" v-if="log.img.endsWith('dwg')"  class="image-link" @click.prevent="viewDwg(log.img)">查看文件</a>
                <a href="#" v-else class="image-link" @click.prevent="previewImage(log.img)">查看图片</a>
              </template>
              <template v-else>
                <span v-if="log.remark" class="text-grey">{{ log.remark }}</span>
              </template>
            </div>
          </div>
        </v-list-item>
      </v-list>

      <div v-else class="text-center py-4 text-white">
        暂无操作记录
      </div>
    </div>

    <!-- 图片预览对话框 -->
    <ImagePreviewDialog
      v-model="previewDialogVisible"
      :image-url="previewImageUrl"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import ImagePreviewDialog from './ImagePreviewDialog.vue'
import { productDrawingApi, drawingApi } from '@/services/api'

// 设置语言为中文
dayjs.locale('zh-cn')

const props = defineProps<{
  productOrderId?: number | string
  productOrderItemId?: number | string
  type: number
}>()

const logs = ref<any[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// 图片预览相关
const previewDialogVisible = ref(false)
const previewImageUrl = ref('')

const emit = defineEmits<{
  (e: 'error', msg: string): void
}>()

// 预览图片
const previewImage = (url: string) => {
  previewImageUrl.value = url
  previewDialogVisible.value = true
}
// 查看CAD文件
const viewDwg = (file: any) => {
  console.log('查看CAD文件', file)
  window.open('http://cad2.jiangfengoa.com?dwg='+file.url, '_blank')
}
// 获取操作颜色
const getOperationColor = (operate: number) => {
  switch (operate) {
    case 0:
      return 'success'
    case 1:
      return 'primary'
    case 2:
      return 'error'
    case 3:
      return 'warning'
    case 4:
      return 'info'
    case 5:
      return 'green-darken-1'
    case 6:
      return 'orange'
    case 7:
      return 'red'
    case 8:
      return 'red-darken-2'
    default:
      return 'grey'
  }
}

// 获取操作文本
const getOperationText = (operate: number) => {
  switch (operate) {
    case 0:
      return '上传'
    case 1:
      return '替换'
    case 2:
      return '删除'
    case 3:
      return '提交审核'
    case 4:
      return '财务审核通过'
    case 5:
      return '集团审核通过'
    case 6:
      return '分配工厂'
    case 7:
      return '财务审核拒绝'
    case 8:
      return '集团审核拒绝'
    default:
      return '未知'
  }
}

// 格式化时间
const formatTime = (time: string | number) => {
  return dayjs(Number(time)).format('MM-DD HH:mm:ss')
}

// 获取日志数据
const fetchLogs = async () => {
  if (!props.productOrderId && !props.productOrderItemId) return
  
  loading.value = true
  error.value = null
  
  try {
    const params = props.type === 0 
      ? {
          type: props.type,
          productOrderId: Number(props.productOrderId)
        }
      : {
          type: props.type,
          productOrderItemId: Number(props.productOrderItemId)
        }
    
    const res = props.type === 0
      ? await drawingApi.getDrawLog(params)
      : await productDrawingApi.getDrawLog(params)
    
    if (res.code === 0) {
      logs.value = (res.data || []).sort((a, b) => Number(b.createTime) - Number(a.createTime))
    } else {
      logs.value = []
    }
  } catch (err: any) {
    console.error('获取操作日志失败:', err)
    logs.value = []
  } finally {
    loading.value = false
  }
}

// 监听属性变化
watch(
  [() => props.productOrderId, () => props.productOrderItemId],
  () => {
    fetchLogs()
  }
)

// 初始化
onMounted(() => {
  fetchLogs()
})

// 暴露方法
defineExpose({
  fetchLogs
})
</script>

<style scoped>
.operation-log {
  width: 800px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 4px;
  background-color: #2B2D33;
  color: #ffffff;
}

.log-header {
  background-color: rgba(255, 255, 255, 0.05);
}

.log-content {
  max-height: 400px;
  overflow-y: auto;
}

.log-content::-webkit-scrollbar {
  width: 8px;
}

.log-content::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
}

.log-content::-webkit-scrollbar-thumb {
  background: rgb(var(--v-theme-primary));
  border-radius: 4px;
}

.log-content::-webkit-scrollbar-thumb:hover {
  background: rgb(var(--v-theme-primary));
  opacity: 0.8;
}

.log-list {
  background: transparent;
}

.log-item {
  padding: 8px 16px;
  min-height: 40px;
  background-color: rgba(255, 255, 255, 0.03);
  margin-bottom: 4px;
  border-radius: 4px;
}

.log-item:nth-child(even) {
  background-color: rgba(255, 255, 255, 0.05);
}

.log-row {
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
}

.log-time {
  min-width: 120px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.7);
}

.log-operator {
  min-width: 80px;
  flex-shrink: 0;
  color: rgba(255, 255, 255, 0.9);
}

.log-image {
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
}

.image-link {
  color: #64B5F6;
  text-decoration: none;
}

.image-link:hover {
  text-decoration: underline;
  color: #90CAF9;
}

.preview-image {
  width: 100%;
  height: auto;
  max-height: 80vh;
  object-fit: contain;
}
</style>
